import { type Accessor, createSignal, onCleanup, onMount } from "solid-js";
import { getRealtimeEcho } from "~/modules/shared/utils/realtime";

type UseWebSocketOptions<T> = {
	/** Private channel name, without the `private-` prefix. Nothing subscribes while undefined. */
	channel: string | undefined;
	/** Broadcast name as Echo expects it, e.g. `.connected-accounts-stats.updated`. */
	event: string;
	onMessage: (payload: T) => void;
	/**
	 * Runs each time messages start arriving, including after a reconnect.
	 * Anything broadcast while offline is lost, so callers refetch here.
	 */
	onLive?: () => void;
};

/** Callers per channel name. The channel is only left once the last one cleans up. */
const channelUsers = new Map<string, number>();

/**
 * Listens for one event on a private Echo channel. `isLive` is true only while
 * the socket is connected and the channel is subscribed, so callers can fall
 * back to polling whenever messages aren't arriving.
 */
export function useWebSocket<T>(options: UseWebSocketOptions<T>): {
	isLive: Accessor<boolean>;
} {
	const [isLive, setIsLive] = createSignal(false);

	onMount(() => {
		const echo = getRealtimeEcho();
		const channelName = options.channel;
		if (!echo || !channelName) {
			return;
		}

		// Echo hands every caller the same channel, so a later caller never
		// sees the subscription event. Pusher's own flag is read instead: it
		// clears on every drop and sets again once the channel resubscribes.
		const channel = echo.private(channelName);
		const subscription = channel.subscription;
		channelUsers.set(channelName, (channelUsers.get(channelName) ?? 0) + 1);

		const syncLive = () => {
			const live =
				echo.connectionStatus() === "connected" && subscription.subscribed;
			if (live && !isLive()) {
				options.onLive?.();
			}
			setIsLive(live);
		};
		const onMessage = (payload: T) => options.onMessage(payload);

		subscription.bind("pusher:subscription_succeeded", syncLive);
		subscription.bind("pusher:subscription_error", syncLive);
		const stopWatchingConnection = echo.connector.onConnectionChange(syncLive);
		channel.listen(options.event, onMessage);
		syncLive();

		onCleanup(() => {
			stopWatchingConnection();
			subscription.unbind("pusher:subscription_succeeded", syncLive);
			subscription.unbind("pusher:subscription_error", syncLive);
			channel.stopListening(options.event, onMessage);
			const users = (channelUsers.get(channelName) ?? 1) - 1;
			if (users > 0) {
				channelUsers.set(channelName, users);
			} else {
				channelUsers.delete(channelName);
				echo.leave(channelName);
			}
			setIsLive(false);
		});
	});

	return { isLive };
}
